import { GroupingRule, Named, ViewIds } from './apiTypes';
import { ArdoqId, Entity, MetaData, Versioned } from './coreTypes';
import { DashboardFilterValues } from './dashboardAPITypes';
import { FilterAttributes, WorkspaceFilterAttributes } from './filterApiTypes';
import { PersistedLoadedStateParams } from './loadedState';
import { ReportSlideParams } from './reportAPITypes';

export enum SlideTypes {
  VISUALIZATION = 'visualization',
  DASHBOARD = 'dashboard',
  REPORT = 'report',
  METAMODEL = 'metamodel',
  LUCID = 'lucid',
}

/**
 * @see src/ardoq/persistence/slide_repo.clj
 */
export type APISlideAttributes =
  | DashboardSlide
  | MetamodelSlide
  | ReportSlide
  | VisualizationSlide
  | LucidSlide;

export type DashboardSlideParams = {
  globalFilterValues?: DashboardFilterValues;
};

export type DashboardSlide = BaseSlide & {
  type: SlideTypes.DASHBOARD;
  dashboardId: ArdoqId;
  dashboardParams: DashboardSlideParams | null;
};

type SpecificMetamodelSlide = BaseSlide & {
  type: SlideTypes.METAMODEL;
  metamodelId: ArdoqId;
};

type FullOrgMetamodelSlide = BaseSlide & {
  type: SlideTypes.METAMODEL;
  fullOrg: true;
  metamodelId: ArdoqId | null;
};

export type MetamodelSlide = SpecificMetamodelSlide | FullOrgMetamodelSlide;

export const isFullOrgMetamodelSlide = (
  slide: MetamodelSlide
): slide is FullOrgMetamodelSlide =>
  slide && 'fullOrg' in slide && slide.fullOrg;

export const isSpecificMetamodelSlide = (
  slide: MetamodelSlide
): slide is SpecificMetamodelSlide => slide && !isFullOrgMetamodelSlide(slide);

export type ReportSlide = BaseSlide & {
  type: SlideTypes.REPORT;
  reportId: ArdoqId;
  reportParams?: ReportSlideParams;
};

export type DynamicFilterParams = Record<string, string>;

export interface ApiDynamicFilter {
  storedQueryId: string;
  params: DynamicFilterParams;
}

export type VisualizationSlide = BaseSlide & {
  type: SlideTypes.VISUALIZATION;
  workspaceId: ArdoqId;
  consideredWorkspaceIds: ArdoqId[] | null;
  filters: Filters;
  dynamicFilters?: ApiDynamicFilter[] | null;

  view: ViewIds;
  viewstate: Record<string, any>;

  componentId?: ArdoqId | null;
  componentName?: string | null;
  workspaceIds?: ArdoqId[] | null;
  sortAttr?: string | null;
  sortOrder?: number | null;
  scenarioId?: ArdoqId | null;
  isInVisualDiffMode?: boolean | null;
  /** scenario slide where the view was open on a workspace or the entire scenario (in the navigator) at time of saving */
  /** will be null if not a scenario slide OR if a scenario slide, where the view was open on a specific component (in the navigator) at the time of saving */
  selectedScenarioId?: ArdoqId | null;
  thumbnail?: string;
  groupBys?: GroupingRule[] | null;
  /** Workspaces editing user has read access to under the workspace permission model */
  fullAccessWorkspaceIds?: ArdoqId[] | null;
};

export type LucidSlide = BaseSlide & {
  type: SlideTypes.LUCID;
  lucidDocumentId: string;
};

type BaseSlide = Entity &
  MetaData &
  Named &
  Versioned & {
    type: SlideTypes;
    description: string; // Can't use described as it's nullable
    loadedStates?: PersistedLoadedStateParams[];
  };

type Filters = {
  advancedFilters: FilterAttributes[];
  workspaceFilter: WorkspaceFilterAttributes | undefined;
};

export const isDashboardSlide = (
  attributes: APISlideAttributes
): attributes is DashboardSlide => attributes.type === SlideTypes.DASHBOARD;

export const isMetamodelSlide = (
  attributes: APISlideAttributes
): attributes is MetamodelSlide => attributes.type === SlideTypes.METAMODEL;

export const isReportSlide = (
  attributes: APISlideAttributes
): attributes is ReportSlide => attributes.type === SlideTypes.REPORT;

export const isVisualizationSlide = (
  attributes: APISlideAttributes
): attributes is VisualizationSlide =>
  attributes.type === SlideTypes.VISUALIZATION;

export const isLucidSlide = (
  attributes: APISlideAttributes
): attributes is LucidSlide => {
  return attributes.type === SlideTypes.LUCID;
};
